<template lang="pug">
    .event-slot-picker
        paginated-items(
            :scope="scope"
            :type="key"
            :loading="loading"
            :items="items"
            :show-filters="showFilters"
            :filters="filters"
            :sortables="sortables"
            :sort="sort"
            :variables="variables"
            :all-columns="allColumns"
            :active-columns="allColumns"
            :page-info="pageInfo"
            @change-filter="changeFilter"
            @change-sort="changeSort"
            @pagination-size-change="changePaginationSize"
            @pagination-current-change="changeCurrentPage"
        )
            template(slot="top")
                el-row.table-top(type="flex" justify="space-between")
                    .left
                        span.count {{ selectionSummary }}
                        //- One full date makes the whole ticket unsellable, because it admits its
                        //- holder to every date it covers.
                        el-tag.sold-out-warning(v-if="selectedIsSoldOut" size="mini" type="danger")
                            i.fas.el-icon-fa-exclamation-triangle
                            span {{ $t('Not sellable: a selected slot is fully booked') }}
                    .right
                        el-button(
                            plain
                            size="mini"
                            type="primary"
                            icon="el-icon-fa-filter fas"
                            @click="toggleFilterVisiblity"
                        )
                            span(v-if="showFilters") {{ $t('Hide filters') }}
                            span(v-else) {{ $t('Show filters') }}

            //- The platform's own bulk selection: "all" is held as an exclusive selection rather
            //- than a list of ids, which is what makes an event with thousands of dates workable.
            template(v-slot:actions:th)
                .col-left(v-if="!showFilters")
                    check-all(
                        :total="pageInfo.total"
                        :show-dropdown="pageInfo.lastPage > 1"
                        :checked="bulkSelection"
                        :is-indeterminate="selectionIndeterminate"
                        @check-all="checkAll"
                        @check-visible="checkVisible"
                    )
            template(v-slot:actions:filter)
                .col-left(v-if="showFilters")
                    check-all(
                        :total="pageInfo.total"
                        :show-dropdown="pageInfo.lastPage > 1"
                        :checked="bulkSelection"
                        :is-indeterminate="selectionIndeterminate"
                        @check-all="checkAll"
                        @check-visible="checkVisible"
                    )

            template(v-slot:actions="{ item }")
                el-checkbox(
                    :key="item.id"
                    :value="isChecked(item.id)"
                    @change="checkItem(item)"
                )

            template(v-slot:start_at="{ item }")
                span {{ item.start_at | timestamp2date('dd.MM.yyyy, HH:mm') }}

            template(v-slot:end_at="{ item }")
                span {{ formatSlotEnd(item) }}

            template(v-slot:available_seats="{ item }")
                span(v-if="item.available_seats === null") {{ $t('unlimited') }}
                span(v-else-if="item.available_seats < 1" class="is-full") {{ $t('fully booked') }}
                span(v-else) {{ item.available_seats }}
</template>

<i18n>
{
    "de": {
        "Until": "bis",
        "Seats left": "Freie Plätze",
        "unlimited": "unbegrenzt",
        "{selected} of {total} slots selected": "{selected} von {total} Slots ausgewählt",
        "Slot": "Slot",
        "fully booked": "ausgebucht",
        "Not sellable: a selected slot is fully booked": "Nicht verkäuflich: ein ausgewählter Slot ist ausgebucht",
        "Show filters": "Filter anzeigen",
        "Hide filters": "Filter verbergen",
        "from": "von",
        "to": "bis",
        "Comment": "Kommentar",
        "An error occurred!": "Ein Fehler ist aufgetreten!"
    },
    "en": {
        "{selected} of {total} slots selected": "{selected} of {total} slots selected"
    }
}
</i18n>

<script>
import _ from 'lodash';

import CheckAll from '@/components/CheckAll';
import PaginatedItems from '@/components/PaginatedItems';
// timestamp2date is not registered globally (see filters.js) - Vue falls back to the identity
// function for an unknown filter, so without this the raw unix timestamps render.
import { timestamp2date } from '@/filters';
import Vue from 'vue';
import EventTicketApi from '@/extensions/shop/store/api/eventTicket';

/**
 * The dates a ticket admits to.
 *
 * Server-paginated through the platform's own pagination query and list component: an event can
 * carry thousands of dates, so loading them all and rendering a checkbox per row hung both hosts.
 * The selection is held as ids and therefore survives paging and filtering.
 */
export default {
    name: 'EventSlotPicker',
    components: {
        CheckAll,
        PaginatedItems,
    },
    filters: {
        timestamp2date,
    },
    props: {
        eventId: {
            type: [Number, String],
            default: null,
        },
        // The selected slots, as the product carries them: [{ id }].
        value: {
            type: Array,
            default: () => [],
        },
        // Tick everything the event has when nothing is selected yet. True while creating (selling
        // the whole course is the common case), false when editing - there an empty selection is
        // the merchant's own doing.
        selectAllWhenEmpty: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            scope: 'ex',
            key: 'evnt_slot',
            loading: false,
            items: [],
            showFilters: false,
            filters: {},
            sortables: ['start_at'],
            sort: { field: 'start_at', direction: 'asc' },
            selected: [],
            // selection follows the platform's bulk model: 'exclusive' with no identifiers means
            // "everything", which is how a thousand-date event stays workable.
            pageInfo: {
                total: 0,
                currentPage: 1,
                lastPage: 1,
                perPage: 10,
                selection: { type: 'inclusive', identifiers: [], total: 0 },
            },
            variables: { paginationSizes: [10, 25, 50, 100] },
        };
    },
    computed: {
        selection() {
            return this.pageInfo.selection;
        },
        selectedCount() {
            return this.selection.type === 'exclusive'
                ? this.pageInfo.total - this.selection.identifiers.length
                : this.selection.identifiers.length;
        },
        /**
         * A ticket admits its holder to every slot it covers, so one full slot makes the whole
         * ticket undeliverable - the storefront refuses it, and the merchant should know why.
         */
        selectedIsSoldOut() {
            return this.items.some(item =>
                this.isChecked(item.id)
                && item.available_seats !== null
                && item.available_seats < 1
            );
        },
        selectionSummary() {
            return this.$t('{selected} of {total} slots selected', {
                selected: this.selectedCount,
                total: this.pageInfo.total,
            });
        },
        bulkSelection() {
            let checked = 'none';

            if (this.selection.type === 'exclusive' && this.selection.identifiers.length < this.pageInfo.total) {
                checked = 'all';
            } else if (this.selection.type === 'inclusive' && this.selection.identifiers.length) {
                checked = 'visible';
            }

            return checked;
        },
        selectionIndeterminate() {
            if (this.selection.type !== 'inclusive' || !this.selection.identifiers.length) return false;

            const onPage = this.items.filter(item => this.isChecked(item.id)).length;

            return onPage > 0 && onPage < this.items.length;
        },
        allColumns() {
            const fromTo = [
                { key: 'from', placeholder: this.$t('from'), position: 10 },
                { key: 'to', placeholder: this.$t('to'), position: 20 },
            ];

            return [
                {
                    key: 'start_at',
                    name: this.$t('Slot'),
                    value_path: 'start_at',
                    filter: { type: 'DateTimeFromTo', options: fromTo },
                },
                { key: 'end_at', name: this.$t('Until'), value_path: 'end_at' },
                {
                    key: 'comment',
                    name: this.$t('Comment'),
                    value_path: 'comment',
                    filter: { type: 'SingleInput' },
                },
                { key: 'available_seats', name: this.$t('Seats left'), value_path: 'available_seats' },
            ];
        },
    },
    watch: {
        eventId() {
            this.resetSelection();
            this.range = null;
            this.reload();
        },
        value: {
            immediate: true,
            handler(value) {
                const incoming = (value || []).map(slot => parseInt(slot.id));

                // Only adopt what the host holds; echoing our own emit back would fight the user.
                if (!_.isEqual(_.sortBy(incoming), _.sortBy(this.resolvedIds()))) {
                    this.pageInfo.selection = {
                        type: 'inclusive',
                        identifiers: incoming,
                        total: incoming.length,
                    };
                }
            },
        },
    },
    methods: {
        /**
         * The end time alone, unless the date ends on a different day - an overnight or multi-day
         * slot reading "17:00 bis 18:00" would look like it ends the same evening.
         */
        formatSlotEnd(slot) {
            const sameDay = timestamp2date(slot.start_at, 'yyyy-MM-dd')
                === timestamp2date(slot.end_at, 'yyyy-MM-dd');

            return timestamp2date(slot.end_at, sameDay ? 'HH:mm' : 'dd.MM.yyyy, HH:mm');
        },
        isChecked(id) {
            const identifiers = this.selection.identifiers.map(el => parseInt(el));

            return this.selection.type === 'exclusive'
                ? !identifiers.includes(parseInt(id))
                : identifiers.includes(parseInt(id));
        },
        resetSelection() {
            this.pageInfo.selection = { type: 'inclusive', identifiers: [], total: 0 };
        },
        resolvedIds() {
            return this.selection.type === 'inclusive'
                ? this.selection.identifiers.map(el => parseInt(el))
                : null;
        },
        /**
         * The host stores concrete slot ids, so an "everything" selection is resolved to ids once,
         * here - ids only, so it stays cheap even for a long-running event.
         */
        async emitSelection() {
            const ids = this.resolvedIds();

            if (ids !== null) {
                this.$emit('input', ids.map(id => ({ id })));
                return;
            }

            this.loading = true;

            try {
                const data = await EventTicketApi.fetchSlotsPaginated({
                    eventID: this.eventId,
                    page: 1,
                    perPage: Math.max(this.pageInfo.total, 1),
                });

                const excluded = this.selection.identifiers.map(el => parseInt(el));
                const all = (data.items || [])
                    .map(slot => parseInt(slot.id))
                    .filter(id => !excluded.includes(id));

                this.$emit('input', all.map(id => ({ id })));
            } catch (error) {
                this.reportError(error);
            } finally {
                this.loading = false;
            }
        },
        async checkAll({ value }) {
            const selection = this.selection;

            if (value === true) {
                selection.type = 'exclusive';
                selection.identifiers = [];
                selection.total = this.pageInfo.total;
            } else {
                selection.type = 'inclusive';
                selection.identifiers = [];
                selection.total = 0;
            }

            await this.emitSelection();
        },
        checkVisible({ value }) {
            const ids = this.items.map(item => parseInt(item.id));
            const selection = this.selection;

            selection.type = 'inclusive';
            selection.identifiers = value === true
                ? [...new Set([...selection.identifiers.map(el => parseInt(el)), ...ids])]
                : selection.identifiers.map(el => parseInt(el)).filter(id => !ids.includes(id));

            selection.total = selection.identifiers.length;

            this.emitSelection();
        },
        checkItem(item) {
            const id = parseInt(item.id);
            const selection = this.selection;
            const identifiers = selection.identifiers.map(el => parseInt(el));
            const listed = identifiers.includes(id);

            // Under an exclusive selection the identifiers are the exceptions, so ticking a row
            // removes it from that list rather than adding it.
            selection.identifiers = listed
                ? identifiers.filter(el => el !== id)
                : [...identifiers, id];

            selection.total = selection.type === 'exclusive'
                ? this.pageInfo.total - selection.identifiers.length
                : selection.identifiers.length;

            this.emitSelection();
        },
        /**
         * The column filters, translated into what the paginated slot query understands.
         */
        serverFilters() {
            const start = this.filters.start_at || {};
            const comment = this.filters.comment || {};

            return {
                from: start.from || null,
                to: start.to || null,
                comment: comment.like || null,
            };
        },
        changeFilter(key, value) {
            const empty = value.match === '' && value.like === ''
                && value.from === '' && value.to === ''
                && (value.in || []).length === 0;

            if (empty) Vue.delete(this.filters, key);
            else Vue.set(this.filters, key, value);

            this.load(1);
        },
        toggleFilterVisiblity() {
            this.showFilters = !this.showFilters;
        },
        changeSort(field) {
            this.sort = {
                field,
                direction: this.sort.field === field && this.sort.direction === 'asc' ? 'desc' : 'asc',
            };

            this.load(1);
        },

        changeCurrentPage(page) {
            this.load(page);
        },
        changePaginationSize(size) {
            this.pageInfo.perPage = size;
            this.load(1);
        },
        reload() {
            this.load(1);
        },
        async load(page = 1) {
            if (!this.eventId) {
                this.items = [];
                this.pageInfo = { ...this.pageInfo, total: 0, currentPage: 1, lastPage: 1 };
                return;
            }

            this.loading = true;

            try {
                const data = await EventTicketApi.fetchSlotsPaginated({
                    eventID: this.eventId,
                    page,
                    perPage: this.pageInfo.perPage,
                    order: this.sort.field,
                    direction: this.sort.direction,
                    ...this.serverFilters(),
                });

                this.items = data.items || [];
                this.pageInfo = { ...this.pageInfo, ...(data.pageInfo || {}), selection: this.selection };

                // A new ticket sells the whole event unless narrowed: unticking a slot is less
                // work than ticking ten, and a course sold as one ticket is the common case.
                if (this.selectAllWhenEmpty && !this.selectedCount && this.pageInfo.total) {
                    await this.checkAll({ value: true });
                }

            } catch (error) {
                this.reportError(error);
            } finally {
                this.loading = false;
            }
        },
        reportError(error) {
            /* eslint no-console: ["error", { allow: ["error"] }] */
            console.error(error);

            this.$notify.error({
                title: this.$t('An error occurred!'),
                iconClass: 'fas el-icon-fa-times',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000,
            });
        },
    },
    mounted() {
        this.reload();
    },
};
</script>

<style lang="sass" scoped>
.event-slot-picker
    .table-top
        align-items: center

        .count
            margin-right: 1rem
            font-size: 12px
            color: #606266

            strong
                font-size: 14px
                color: #303133

    .is-full
        color: #F56C6C

    .sold-out-warning
        margin-left: .75rem

        i
            margin-right: .35rem
</style>
